import React from "react";
import {
  PhoneCall,
  Workflow,
  Filter,
  LayoutDashboard,
  BrainCircuit,
  Smartphone,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    id: 1,
    icon: PhoneCall,
    title: "AI Receptionists",
    desc: "24/7 AI-powered call handling and lead qualification that sounds human and never sleeps.",
    tags: ["Inbound", "Qualification"],
  },
  {
    id: 2,
    icon: Workflow,
    title: "Automation Systems",
    desc: "Automate workflows, follow-ups, operations, and customer communication end-to-end.",
    tags: ["Workflows", "Ops"],
  },
  {
    id: 3,
    icon: Filter,
    title: "Lead Funnels",
    desc: "High-converting funnels designed to attract, qualify, and route the right leads.",
    tags: ["Conversion", "Routing"],
  },
  {
    id: 4,
    icon: LayoutDashboard,
    title: "Website Design",
    desc: "Premium websites built for speed, trust, and conversion — engineered like a product.",
    tags: ["Performance", "UX"],
  },
  {
    id: 5,
    icon: BrainCircuit,
    title: "Custom AI Solutions",
    desc: "Tailored AI integrations designed around your business — agents, copilots, automations.",
    tags: ["LLMs", "Agents"],
  },
  {
    id: 6,
    icon: Smartphone,
    title: "Mobile App Development",
    desc: "Custom mobile apps and customer experiences that deepen engagement and retention.",
    tags: ["iOS", "Android"],
  },
];

const ServiceCard = ({ s, index }) => (
  <div
    className="group relative overflow-hidden rounded-2xl bg-[#0A0A0A] border border-white/5 p-7 transition-all duration-500 hover:-translate-y-1 hover:border-[#39FF14]/30 float-card"
    data-testid={`service-card-${s.id}`}
  >
    {/* hover sheen */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
      <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[#39FF14]/20 via-transparent to-[#007AFF]/15 blur-md" />
    </div>
    {/* corner number */}
    <div className="absolute top-5 right-5 text-[10px] font-mono text-zinc-600">
      0{index + 1}
    </div>

    <div className="relative">
      <div className="svc-icon">
        <s.icon className="w-6 h-6" />
      </div>
      <h3 className="mt-6 text-xl md:text-2xl font-semibold text-white tracking-tight">
        {s.title}
      </h3>
      <p className="mt-3 text-zinc-400 leading-relaxed">{s.desc}</p>

      <div className="mt-6 flex items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {s.tags.map((t) => (
            <span
              key={t}
              className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 text-zinc-400"
            >
              {t}
            </span>
          ))}
        </div>
        <ArrowUpRight className="w-5 h-5 text-zinc-600 group-hover:text-[#39FF14] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
      </div>
    </div>
  </div>
);

export default function Services() {
  return (
    <section
      id="services"
      className="relative section-padding"
      data-testid="services-section"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14 reveal">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
              <span className="w-6 h-px bg-[#39FF14]" />
              Solutions
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Systems engineered <br className="hidden md:block" />
              for <span className="gradient-text-green">growth.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            Six core capabilities — composed into one operating system for
            modern businesses. Choose one, or let us build the whole stack.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
          {services.map((s, i) => (
            <ServiceCard s={s} index={i} key={s.id} />
          ))}
        </div>
      </div>
    </section>
  );
}
